import React, { useState, useEffect, useRef, useMemo } from 'react';
import { rptService } from '../../../../services/rptService';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  IconButton,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  History as HistoryIcon,
  Check as CheckIcon,
  Help as HelpIcon,
  Settings as SettingsIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Delete as DeleteIcon,
  Print as PrintIcon
} from '@mui/icons-material';

// Helper for rounding to 2 decimal places
const round2 = (num) => Math.round((num + Number.EPSILON) * 100) / 100;

const PropertyPaymentTab = ({ property, currentUser, showSnackbar, showConfirm, requestPaymentInfo, onAddCartItem }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0: calculation, 1: history
  
  // Tax calculation states
  const [yearFrom, setYearFrom] = useState(new Date().getFullYear() - 5);
  const [yearTo, setYearTo] = useState(new Date().getFullYear());
  const [calculatedTax, setCalculatedTax] = useState(null);
  const [yearBreakdown, setYearBreakdown] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [availablePayors, setAvailablePayors] = useState([]);
  const [selectedPayors, setSelectedPayors] = useState([]);
  const [showDiscountHelp, setShowDiscountHelp] = useState(false);
  
  // Payment history states
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deletingPaymentId, setDeletingPaymentId] = useState(null);

  // Initialize defaults when property changes
  useEffect(() => {
    if (property) {
      loadDefaults();
      // Parse payors
      const parsePayorsFromRemarks = (remarks) => {
        if (!remarks) return { count: 1, names: [] };
        const lines = String(remarks).split('\n');
        const first = lines[0] || '';
        if (first.startsWith('PAYORS_JSON:')) {
          try {
            const payload = JSON.parse(first.replace('PAYORS_JSON:', ''));
            return { count: payload.count || 1, names: payload.names || [] };
          } catch (e) {
            return { count: 1, names: [] };
          }
        }
        return { count: 1, names: [] };
      };
      const parsed = parsePayorsFromRemarks(property.remarks || '');
      const names = parsed.names && parsed.names.length ? parsed.names : Array.from({ length: parsed.count || 1 }, () => '');
      setAvailablePayors(names);
      setSelectedPayors(names.filter(n => n && n.trim()));
    }
  }, [property]);

  const loadDefaults = async () => {
    setCalculatedTax(null);
    setYearBreakdown([]);
    
    // Fetch last payment for this property to set default years
    try {
      const result = await rptService.getPaymentsByProperty(property.property_id);
      if (result.success) {
        const propertyPayments = result.data; 
        const currentYear = new Date().getFullYear();
        
        if (propertyPayments.length > 0) {
          const maxYearTo = Math.max(...propertyPayments.map(p => p.year_to));
          const nextYear = maxYearTo + 1;
          setYearFrom(nextYear);
          setYearTo(currentYear);
        } else {
          setYearFrom(currentYear - 5);
          setYearTo(currentYear);
        }
      }
    } catch (error) {
      console.error('Error fetching last payment:', error);
      const currentYear = new Date().getFullYear();
      setYearFrom(currentYear - 5);
      setYearTo(currentYear);
    }
  };

  useEffect(() => {
    if (activeTab === 1 && property) {
      loadPaymentHistory();
    }
  }, [activeTab, property]);

  // Derived payment ratio for display
  const paymentRatio = useMemo(() => {
    const total = availablePayors.length;
    const selected = selectedPayors.length;
    if (total > 1) {
      if (selected > 0 && selected < total) {
        return selected / total;
      }
    }
    return 1;
  }, [availablePayors.length, selectedPayors.length]);

  const loadPaymentHistory = async () => {
    if (!property) return;
    setLoadingHistory(true);
    try {
      const result = await rptService.getPaymentsByProperty(property.property_id);
      if (result.success) {
        setPaymentHistory(result.data);
      }
    } catch (error) {
      console.error('Error loading payment history:', error);
      showSnackbar('Failed to load payment history', 'error');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleCalculateTax = async () => {
    if (!yearFrom || !yearTo) {
      showSnackbar('Please provide both From Year and To Year.', 'warning');
      return;
    }

    if (yearFrom > yearTo) {
      showSnackbar('From Year must be less than or equal to To Year.', 'warning');
      return;
    }

    setIsCalculating(true);
    try {
      const assessmentsResult = await rptService.getPropertyAssessments(property.property_id);
      
      if (!assessmentsResult.success || assessmentsResult.data.length === 0) {
        showSnackbar('This property has no assessed values. Please add at least one assessment before calculating taxes.', 'warning');
        setIsCalculating(false);
        return;
      }

      const assessments = assessmentsResult.data;
      const amnestyResult = await rptService.getAllAmnesty();
      const amnestyRecords = amnestyResult.success ? (amnestyResult.data || []) : [];
      const sortedAssessments = [...assessments].sort((a, b) => a.effectivity_year - b.effectivity_year);

      const breakdown = [];
      let totalTax = 0;

      for (let year = yearFrom; year <= yearTo; year++) {
        let applicableAssessment = null;
        for (let i = sortedAssessments.length - 1; i >= 0; i--) {
          if (sortedAssessments[i].effectivity_year <= year) {
            applicableAssessment = sortedAssessments[i];
            break;
          }
        }

        if (applicableAssessment) {
          const assessedValue = parseFloat(applicableAssessment.assessed_value);
          const taxDue = assessedValue * 0.01;
          const fullPayment = round2(taxDue * 2);
          
          const today = new Date();
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth() + 1;
          
          let discount = 0;
          let discountPercent = 0;
          let penalty = 0;
          let penaltyPercent = 0;
          let status = '';
          let amnestyApplied = false;
          
          const applicableAmnesty = amnestyRecords.find(a => 
            year >= a.tax_year_from && year <= a.tax_year_to
          );
          
          if (applicableAmnesty) {
            penaltyPercent = applicableAmnesty.penalty_percent;
            penalty = round2(fullPayment * (penaltyPercent / 100));
            status = penaltyPercent === 0 ? 'Amnesty' : `Penalty (${penaltyPercent}% penalty)`;
            amnestyApplied = penaltyPercent === 0;
          } else {
            if (year > currentYear) {
              discountPercent = 20;
              discount = round2(fullPayment * 0.20);
              status = 'Advance Payment (20% discount)';
            } else if (year === currentYear) {
              if (currentMonth >= 1 && currentMonth <= 3) {
                discountPercent = 10;
                discount = round2(fullPayment * 0.10);
                status = 'Early Payment (10% discount)';
              } else if (currentMonth >= 4) {
                const monthsSinceApril = currentMonth - 4 + 1;
                penaltyPercent = Math.min(8 + (monthsSinceApril - 1) * 2, 72);
                penalty = round2(fullPayment * (penaltyPercent / 100));
                status = `Penalty (${penaltyPercent}%)`;
              }
            } else {
              const yearsPast = currentYear - year;
              let monthsFromTaxYear = 9;
              let monthsFromCompleteYears = (yearsPast - 1) * 12;
              let monthsInCurrentYear = currentMonth;
              
              const totalPenaltyMonths = monthsFromTaxYear + monthsFromCompleteYears + monthsInCurrentYear;
              penaltyPercent = Math.min(8 + (totalPenaltyMonths - 1) * 2, 72);
              penalty = round2(fullPayment * (penaltyPercent / 100));
              status = `Overdue (${penaltyPercent}% penalty)`;
            }
          }
          
          const total = round2(fullPayment - discount + penalty);
          totalTax += total;

          breakdown.push({
            year,
            assessedValue,
            effectivityYear: applicableAssessment.effectivity_year,
            taxDue,
            fullPayment,
            discount,
            discountPercent,
            penalty,
            penaltyPercent,
            status,
            total,
            amnestyApplied
          });
        } else {
          breakdown.push({
            year,
            assessedValue: 0,
            effectivityYear: null,
            taxDue: 0,
            fullPayment: 0,
            discount: 0,
            discountPercent: 0,
            penalty: 0,
            penaltyPercent: 0,
            status: 'No assessment',
            total: 0,
            note: 'No assessment available'
          });
        }
      }

      setYearBreakdown(breakdown);
      setCalculatedTax(round2(totalTax));
      if (typeof onAddCartItem === 'function') {
        const years = `${yearFrom} - ${yearTo}`;
        const fullShare = round2(breakdown.reduce((sum, i) => sum + round2((i.fullPayment || 0) * paymentRatio), 0));
        const discPenShare = round2(breakdown.reduce((sum, i) => sum + round2((i.discount > 0 ? -i.discount : (i.penalty || 0)) * paymentRatio), 0));
        const shareAmount = round2(fullShare + discPenShare);
        const payload = {
          snapshot_id: `${property.property_id}-${yearFrom}-${yearTo}-${selectedPayors?.length || 0}`,
          property_id: property.property_id,
          td_number: property.td_number,
          owner_name: property.owner_name,
          barangay: property.barangay,
          amount: shareAmount,
          full_amount: fullShare,
          discpen_amount: discPenShare,
          total_amount: shareAmount,
          years,
          payment_ratio: paymentRatio,
          breakdown
        };
        onAddCartItem(payload);
      }
    } catch (error) {
      console.error('Error calculating tax:', error);
      showSnackbar('Unable to calculate taxes. Please try again.', 'error');
    } finally {
      setIsCalculating(false);
    }
  };

  const getActualPaymentAmount = () => {
    if (!yearBreakdown || yearBreakdown.length === 0) return 0;
    const full = yearBreakdown.reduce((sum, i) => sum + round2((i.fullPayment || 0) * paymentRatio), 0);
    const discPen = yearBreakdown.reduce((sum, i) => sum + round2((i.discount > 0 ? -i.discount : (i.penalty || 0)) * paymentRatio), 0);
    return round2(full + discPen);
  };

  const handleProcessPayment = async () => {
    if (!calculatedTax && calculatedTax !== 0) {
      showSnackbar('Please calculate taxes first before processing payment.', 'warning');
      return;
    }

    const actualPaymentAmount = getActualPaymentAmount();

    let enteredOrNumber = '';
    let enteredPayerName = '';
    while (true) {
      const info = await requestPaymentInfo();
      if (info === null) return;
      enteredOrNumber = (typeof info === 'string') ? info : (info?.orNumber || '');
      enteredPayerName = (typeof info === 'object' ? (info?.payerName || '') : '');
      if (enteredOrNumber.trim() === '') {
        showSnackbar('Official Receipt number is required.', 'warning');
        continue;
      }
      break;
    }

    showConfirm(
      'Confirm Payment Processing',
      `Process payment with the following details?\n\nOR Number: ${enteredOrNumber.trim()}${enteredPayerName?.trim() ? `\nPaid By: ${enteredPayerName.trim()}` : ''}\nAmount: ₱${actualPaymentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\nProperty: ${property.td_number}\nYears: ${yearFrom} - ${yearTo}`,
      async () => {
        setIsProcessing(true);

        try {
          const selectedPayorNames = Array.isArray(selectedPayors) ? selectedPayors.filter(n => n && n.trim()) : [];
          const payorsLine = (Array.isArray(availablePayors) && availablePayors.length > 1 && selectedPayorNames.length > 0)
            ? `\nPayors for this payment: ${selectedPayorNames.join(', ')}`
            : '';

          const paymentData = {
            property_id: property.property_id,
            owner_id: property.owner_id,
            payment_date: new Date().toISOString().split('T')[0],
            year_from: yearFrom,
            year_to: yearTo,
            amount_paid: actualPaymentAmount,
            payment_method: 'Cash',
            or_number: enteredOrNumber.trim(),
            year_breakdown: yearBreakdown,
            processed_by: currentUser ? `${currentUser.firstname} ${currentUser.lastname}`.trim() || currentUser.email : 'Unknown User',
            remarks: `Payment for years ${yearFrom}-${yearTo}${enteredPayerName?.trim() ? `\nPaid by: ${enteredPayerName.trim()}` : ''}${payorsLine}`
          };

          const result = await rptService.addPayment(paymentData);

          if (result.success) {
            showSnackbar(`Payment of ₱${actualPaymentAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} has been recorded.`, 'success');
            setCalculatedTax(null);
            setYearBreakdown([]);
            if (activeTab === 1) {
              loadPaymentHistory();
            }
          } else {
            throw new Error(result.error);
          }
        } catch (error) {
          console.error('Error processing payment:', error);
          showSnackbar(`Unable to process payment: ${error.message}`, 'error');
        } finally {
          setIsProcessing(false);
        }
      }
    );
  };

  const handleDeletePayment = async (payment) => {
    const id = payment?.id ?? payment?.payment_id;
    if (!id) {
      showSnackbar('Missing payment identifier.', 'error');
      return;
    }

    showConfirm(
      'Delete Payment',
      `Are you sure you want to delete this payment?\n\nOR: ${payment.or_number || 'N/A'}\nDate: ${new Date(payment.payment_date).toLocaleDateString('en-US')}\nAmount: ₱${parseFloat(payment.amount_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
      async () => {
        setDeletingPaymentId(id);
        try {
          const result = await rptService.deletePayment(id);
          if (result.success) {
            showSnackbar('The payment record has been deleted.', 'success');
            loadPaymentHistory();
          } else {
            throw new Error(result.error || 'Unknown error');
          }
        } catch (error) {
          console.error('Delete payment error:', error);
          showSnackbar(`Unable to delete payment: ${error.message}`, 'error');
        } finally {
          setDeletingPaymentId(null);
        }
      }
    );
  };

  const formatCurrencyTruncated = (value) => {
    if (value === undefined || value === null) return '0.00';
    let num = Number(value);
    let s = num.toFixed(10); 
    const decimalPos = s.indexOf('.');
    if (decimalPos !== -1) {
      s = s.substring(0, decimalPos + 3); 
    }
    return Number(s).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const columnTotals = useMemo(() => {
    return yearBreakdown.reduce((acc, item) => {
      const ratio = paymentRatio;
      acc.full += round2(item.fullPayment * ratio);
      acc.discPen += round2((item.discount > 0 ? -item.discount : (item.penalty || 0)) * ratio);
      const rowTotal = round2(item.fullPayment * ratio) - round2(item.discount * ratio) + round2(item.penalty * ratio);
      acc.total += rowTotal;
      return acc;
    }, { full: 0, discPen: 0, total: 0 });
  }, [yearBreakdown, paymentRatio]);

  const handlePrintCalculation = () => {
    if (!calculatedTax && calculatedTax !== 0) {
      showSnackbar('Please calculate taxes first before printing.', 'warning');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const today = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });

    const columnTotals = yearBreakdown.reduce((acc, item) => {
      const ratio = paymentRatio;
      acc.full += round2(item.fullPayment * ratio);
      acc.discPen += round2((item.discount > 0 ? -item.discount : (item.penalty || 0)) * ratio);
      const rowTotal = round2(item.fullPayment * ratio) - round2(item.discount * ratio) + round2(item.penalty * ratio);
      acc.total += rowTotal;
      return acc;
    }, { full: 0, discPen: 0, total: 0 });

    // Keeping HTML structure for print view, but could be improved or replaced with a React-to-Print component later
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Real Property Tax Computation - ${property.td_number}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              padding: 40px; 
              color: #1f2937;
              line-height: 1.6;
            }
            .header {
              text-align: center;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 3px double #047857;
            }
            .header h1 {
              font-size: 24px;
              color: #047857;
              margin-bottom: 5px;
              font-weight: 700;
            }
            .header h2 {
              font-size: 18px;
              color: #6b7280;
              font-weight: 500;
            }
            .date {
              text-align: right;
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-size: 14px;
              font-weight: 700;
              color: #047857;
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
              background: #f9fafb;
              padding: 15px;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
            }
            .info-item {
              font-size: 13px;
            }
            .info-label {
              font-weight: 600;
              color: #6b7280;
            }
            .info-value {
              color: #1f2937;
              font-weight: 500;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              font-size: 12px;
            }
            th {
              background: linear-gradient(135deg, #047857 0%, #059669 100%);
              color: white;
              padding: 10px 8px;
              text-align: left;
              font-weight: 600;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            th:nth-child(n+2) {
              text-align: right;
            }
            td {
              padding: 8px;
              border-bottom: 1px solid #e5e7eb;
            }
            td:nth-child(n+2) {
              text-align: right;
            }
            tbody tr:hover {
              background: #f9fafb;
            }
            .total-row {
              background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
              font-weight: 700;
              border-top: 2px solid #047857;
              border-bottom: 2px solid #047857;
            }
            .total-row td {
              padding: 12px 8px;
              font-size: 14px;
              color: #047857;
            }
            .discount { color: #059669; }
            .penalty { color: #dc2626; }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 2px solid #e5e7eb;
              text-align: center;
              font-size: 11px;
              color: #9ca3af;
            }
            .summary-box {
              background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
              padding: 20px;
              border-radius: 10px;
              text-align: center;
              margin: 20px 0;
              border: 2px solid #059669;
            }
            .summary-label {
              font-size: 12px;
              color: #047857;
              font-weight: 600;
              margin-bottom: 5px;
            }
            .summary-amount {
              font-size: 28px;
              font-weight: 700;
              color: #059669;
            }
            .notes {
              background: #fef3c7;
              border-left: 4px solid #f59e0b;
              padding: 12px;
              margin: 20px 0;
              font-size: 11px;
              color: #92400e;
            }
            @media print {
              body { padding: 20px; }
              .header h1 { font-size: 20px; }
              .header h2 { font-size: 16px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>🏢 REAL PROPERTY TAX COMPUTATION</h1>
            <h2>Municipality Tax Office</h2>
          </div>

          <div class="date">Printed: ${today}</div>

          <div class="section">
            <div class="section-title">Property Information</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">TD Number:</span>
                <span class="info-value">${property.td_number}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Owner:</span>
                <span class="info-value">${property.owner_name || 'Unknown'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Location:</span>
                <span class="info-value">${property.barangay}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Classification:</span>
                <span class="info-value">${property.classification_name ? property.classification_name : 'N/A'}</span>
              </div>
            </div>
          </div>

          <div class="summary-box">
            <div class="summary-label">TOTAL AMOUNT DUE (${yearFrom} - ${yearTo})</div>
            <div class="summary-amount">₱${formatCurrencyTruncated(calculatedTax)}</div>
          </div>
          ${paymentRatio < 1 ? `
          <div class="summary-box" style="background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%); border-color: #0ea5e9;">
            <div class="summary-label" style="color: #0ea5e9;">SELECTED PAYORS SHARE (${selectedPayors.length}/${availablePayors.length})</div>
            <div class="summary-amount" style="color: #0ea5e9;">₱${formatCurrencyTruncated(round2(calculatedTax * paymentRatio))}</div>
          </div>
          ` : ''}

          <div class="section">
            <div class="section-title">Year-by-Year Breakdown</div>
            <table>
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Assessed Value<br><small style="font-weight: normal; font-size: 10px;">(USED FOR YEAR)</small></th>
                  <th>Tax Due<br><small style="font-weight: normal; font-size: 10px;">(1% OF AV)</small></th>
                  <th>Full Payment<br><small style="font-weight: normal; font-size: 10px;">(2× TAX DUE)</small></th>
                  <th>Discount/Penalty</th>
                  <th>Status</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                ${yearBreakdown.map(item => `
                  <tr>
                    <td>
                      <strong>${item.year}</strong>
                      ${item.effectivityYear ? `<br><small style="color: #6b7280;">(AV ${item.effectivityYear})</small>` : ''}
                    </td>
                    <td>₱${item.assessedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>₱${item.taxDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>₱${round2(item.fullPayment * paymentRatio).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td class="${item.discount > 0 ? 'discount' : (item.penalty > 0 ? 'penalty' : '')}">
                      ${item.discount > 0 ? 
                        `-₱${formatCurrencyTruncated(round2(item.discount * paymentRatio))}<br><small>(${item.discountPercent}% off)</small>` :
                        item.penalty > 0 ?
                        `+₱${formatCurrencyTruncated(round2(item.penalty * paymentRatio))}<br><small>(${item.penaltyPercent}% penalty)</small>` :
                        '₱0.00'
                      }
                    </td>
                    <td><small>${item.status}</small></td>
                    <td><strong>₱${formatCurrencyTruncated(round2(item.fullPayment * paymentRatio) - round2(item.discount * paymentRatio) + round2(item.penalty * paymentRatio))}</strong></td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr class="total-row">
                  <td colspan="3" style="text-align: right; font-weight: bold;">TOTALS</td>
                  <td style="font-weight: bold;">₱${columnTotals.full.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td style="font-weight: bold;">₱${formatCurrencyTruncated(columnTotals.discPen)}</td>
                  <td></td>
                  <td style="font-weight: bold;">₱${formatCurrencyTruncated(columnTotals.total)}</td>
                </tr>
                ${paymentRatio < 1 ? `
                <tr>
                  <td colspan="6" style="text-align: right;">SELECTED PAYORS SHARE (${selectedPayors.length}/${availablePayors.length})</td>
                  <td style="font-weight: bold;">₱${formatCurrencyTruncated(round2(calculatedTax * paymentRatio))}</td>
                </tr>
                ` : ''}
              </tfoot>
            </table>
          </div>

          <div class="notes">
            <strong>Notes:</strong><br>
            • Tax Rate: 1% Basic + 1% SEF = 2% of Assessed Value<br>
            • Advance Payment (Future Years): 20% discount<br>
            • Early Payment (Jan-Mar): 10% discount<br>
            • Late Payment: Starts at 8% in April, increases 2% per month (max 72%)<br>
            • This is a computation only. Official Receipt will be issued upon payment.
          </div>

          <div class="footer">
            Generated by Real Property Tax System | ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handlePrintPropertyProfile = async () => {
    if (!property) {
      showSnackbar('No property selected', 'warning');
      return;
    }

    let lastPayment = null;
    let totalPaid = 0;
    if (paymentHistory.length > 0) {
      const sortedHistory = [...paymentHistory].sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));
      lastPayment = sortedHistory[0];
      totalPaid = sortedHistory.reduce((sum, p) => sum + (parseFloat(p.amount_paid || 0) || 0), 0);
    }

    const printDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showSnackbar('Unable to open print window. Please allow pop-ups and try again.', 'error');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Certification - ${property.td_number}</title>
          <style>
            * { box-sizing: border-box; }
            body { margin: 0; font-family: 'Times New Roman', serif; color: #1a1a1a; background: #f9f9f9; }
            .certificate { width: min(800px, calc(100% - 40px)); margin: 40px auto; padding: 60px; border: 8px double #2c3e50; background: #ffffff; position: relative; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
            .certificate::before { content: ''; position: absolute; top: -8px; left: -8px; right: -8px; bottom: -8px; border: 2px solid #3498db; z-index: -1; }
            .header { text-align: center; margin-bottom: 40px; }
            .header h1 { margin: 0; font-size: 2.5rem; font-weight: bold; color: #2c3e50; text-transform: uppercase; letter-spacing: 2px; border-bottom: 2px solid #3498db; padding-bottom: 10px; }
            .header .subtitle { font-size: 1.2rem; color: #7f8c8d; margin-top: 10px; font-style: italic; }
            .seal { position: absolute; top: 20px; right: 20px; width: 80px; height: 80px; border: 3px solid #e74c3c; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; font-weight: bold; color: #e74c3c; background: #ffffff; }
            .content { margin: 40px 0; }
            .section { margin-bottom: 30px; }
            .section-title { font-size: 1.1rem; font-weight: bold; color: #2c3e50; margin-bottom: 10px; text-decoration: underline; }
            .details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
            .detail-item { padding: 15px; background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 5px; }
            .detail-item strong { display: block; font-size: 0.9rem; color: #495057; text-transform: uppercase; letter-spacing: 1px; }
            .detail-item span { font-size: 1rem; color: #212529; }
            .certification { text-align: center; margin: 40px 0; padding: 20px; background: #ecf0f1; border-radius: 10px; }
            .certification p { margin: 0; font-size: 1.1rem; line-height: 1.6; }
            .signature { margin-top: 60px; text-align: center; }
            .signature-line { border-top: 1px solid #000; width: 200px; margin: 0 auto 10px; }
            .signature-text { font-size: 0.9rem; color: #7f8c8d; }
            .footer { margin-top: 40px; text-align: center; font-size: 0.8rem; color: #7f8c8d; border-top: 1px solid #dee2e6; padding-top: 20px; }
            @media print {
              body { margin: 0; background: white; }
              .certificate { margin: 0; width: 100%; box-shadow: none; }
            }
          </style>
        </head>
        <body>
          <div class="certificate">
            <div class="seal">OFFICIAL<br>SEAL</div>
            <div class="header">
              <h1>Certificate of Payment Status</h1>
              <div class="subtitle">Real Property Tax System</div>
            </div>

            <div class="content">
              <div class="section">
                <div class="section-title">Property Details</div>
                <div class="details">
                  <div class="detail-item">
                    <strong>TD Number</strong>
                    <span>${property.td_number || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <strong>Owner</strong>
                    <span>${property.owner_name || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <strong>PIN</strong>
                    <span>${property.pin || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <strong>Barangay</strong>
                    <span>${property.barangay || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <strong>Kind</strong>
                    <span>${property.kind_name || 'N/A'}</span>
                  </div>
                  <div class="detail-item">
                    <strong>Classification</strong>
                    <span>${property.classification_name || 'N/A'}</span>
                  </div>
                  ${property.address ? `<div class="detail-item"><strong>Address</strong><span>${property.address}</span></div>` : ''}
                  ${property.assessed_value ? `<div class="detail-item"><strong>Assessed Value</strong><span>₱${parseFloat(property.assessed_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>` : ''}
                </div>
              </div>

              <div class="certification">
                <p>This is to certify that the above-described property is <strong>current in its real property tax payments</strong> as of the date indicated below.</p>
                <p>Certification Date: <strong>${printDate}</strong></p>
                ${lastPayment ? `<p>Last payment was recorded on ${new Date(lastPayment.payment_date).toLocaleDateString('en-US')}.</p>` : '<p>No outstanding payments are recorded for this property.</p>'}
              </div>

              <div class="signature">
                <div class="signature-line"></div>
                <div class="signature-text">Authorized Signature</div>
                <div class="signature-text">Real Property Tax Officer</div>
              </div>
            </div>

            <div class="footer">
              This certificate is issued for official municipal purposes only.<br>
              Generated by the Real Property Tax Management System
            </div>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleExport = () => {
    if (activeTab === 1 && paymentHistory.length > 0) {
      const csvContent = [
        ['OR Number', 'Date', 'Amount', 'Covered Years', 'Payer', 'Remarks'],
        ...paymentHistory.map(p => [
          `"${(p.or_number || '').replace(/"/g, '""')}"`,
          `"${new Date(p.payment_date).toLocaleDateString()}"`,
          p.amount_paid,
          `"${p.year_from}-${p.year_to}"`,
          `"${(p.payer_name || '').replace(/"/g, '""')}"`,
          `"${(p.remarks || '').replace(/"/g, '""')}"`
        ])
      ].map(e => e.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payment_history_${property?.td_number || 'export'}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (activeTab === 0 && yearBreakdown.length > 0) {
      const csvContent = [
        ['Year', 'Assessed Value', 'Tax Due', 'Full Payment', 'Discount/Penalty', 'Status', 'Total'],
        ...yearBreakdown.map(item => [
          item.year,
          item.assessedValue,
          item.taxDue,
          item.fullPayment,
          item.discount > 0 ? -item.discount : item.penalty,
          `"${item.status}"`,
          item.total
        ])
      ].map(e => e.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `tax_calculation_${property?.td_number || 'export'}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      showSnackbar('No data to export', 'warning');
    }
  };

  return (
    <Card sx={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', pr: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} aria-label="payment tabs" sx={{ flexGrow: 1 }}>
          <Tab icon={<CalculateIcon />} iconPosition="start" label="Tax Calculation" />
          <Tab icon={<HistoryIcon />} iconPosition="start" label="Payment History" />
        </Tabs>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={activeTab === 0 ? yearBreakdown.length === 0 : paymentHistory.length === 0}
        >
          Export CSV
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PrintIcon />}
          onClick={handlePrintPropertyProfile}
          sx={{ ml: 1 }}
        >
          Print Profile
        </Button>
      </Box>

      <CardContent sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
        {activeTab === 0 && (
          <Grid container spacing={3}>
            {/* Property Details Header & Parameters */}
            <Grid item xs={12} md={5}>
              <Stack spacing={3}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light', borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="h6" color="primary.dark" gutterBottom>
                        {property.td_number}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">PIN: {property.pin}</Typography>
                      <Typography variant="body2" color="text.secondary">Owner: {property.owner_name}</Typography>
                      <Typography variant="body2" color="text.secondary">Barangay: {property.barangay}</Typography>
                      {property.kind_name && (
                        <Typography variant="body2" color="text.secondary">Kind: {property.kind_name}</Typography>
                      )}
                      {property.classification_name && (
                        <Typography variant="body2" color="text.secondary">Classification: {property.classification_name}</Typography>
                      )}
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">Assessed Value</Typography>
                        <Typography variant="h6" color="secondary.main" fontWeight="bold">
                          ₱{parseFloat(property.assessed_value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="subtitle1" fontWeight="bold"><SettingsIcon fontSize="small" /> Parameters</Typography>
                      <Tooltip title="Automatic Discounts & Penalties Info">
                        <IconButton size="small" onClick={() => setShowDiscountHelp(!showDiscountHelp)}>
                          <HelpIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    {showDiscountHelp && (
                      <Alert severity="info" sx={{ mb: 2, fontSize: '0.75rem' }}>
                        <ul style={{ paddingLeft: '1rem', margin: 0 }}>
                          <li>Future years: 20% discount</li>
                          <li>Current year (Jan-Mar): 10% discount</li>
                          <li>Starting April: 8% penalty + 2%/month (max 72%)</li>
                        </ul>
                      </Alert>
                    )}

                    <Stack spacing={2}>
                      <TextField
                        label="From Year"
                        type="number"
                        value={yearFrom}
                        onChange={(e) => setYearFrom(parseInt(e.target.value))}
                        fullWidth
                      />
                      <TextField
                        label="To Year"
                        type="number"
                        value={yearTo}
                        onChange={(e) => setYearTo(parseInt(e.target.value))}
                        fullWidth
                      />
                      
                      {availablePayors && availablePayors.length > 1 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Payors for This Payment</Typography>
                          <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                            {availablePayors.map((name, idx) => {
                              const label = name || `Payor ${idx + 1}`;
                              const checked = selectedPayors.includes(label);
                              return (
                                <Chip
                                  key={idx}
                                  label={label}
                                  size="small"
                                  color={checked ? "primary" : "default"}
                                  variant={checked ? "filled" : "outlined"}
                                  onClick={() => {
                                    setSelectedPayors(prev => {
                                      const set = new Set(prev);
                                      if (checked) set.delete(label);
                                      else set.add(label);
                                      return Array.from(set);
                                    });
                                  }}
                                  icon={checked ? <CheckIcon /> : undefined}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleCalculateTax}
                        disabled={isCalculating}
                        startIcon={<CalculateIcon />}
                      >
                        Calculate Tax
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Calculation Results */}
            <Grid item xs={12} md={7}>
              {calculatedTax !== null ? (
                <Box>
                  <Paper elevation={0} sx={{ p: 2, bgcolor: 'success.lighter', mb: 2, border: '1px solid', borderColor: 'success.light' }}>
                    <Typography variant="subtitle2" color="success.dark">Total Amount Due ({yearFrom} - {yearTo})</Typography>
                    <Typography variant="h3" color="success.dark" fontWeight="bold">
                      ₱{formatCurrencyTruncated(calculatedTax)}
                    </Typography>
                    {paymentRatio < 1 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Selected Payors Share ({selectedPayors.length}/{availablePayors.length}): <strong>₱{formatCurrencyTruncated(round2(calculatedTax * paymentRatio))}</strong>
                      </Typography>
                    )}
                  </Paper>

                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', py: 1.5 }}>YEAR</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', py: 1.5 }}>ASSESSED VALUE<br/><span style={{fontSize: '0.7rem', fontWeight: 'normal'}}>(USED FOR YEAR)</span></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', py: 1.5 }}>TAX DUE<br/><span style={{fontSize: '0.7rem', fontWeight: 'normal'}}>(1% OF AV)</span></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', py: 1.5 }}>FULL PAYMENT<br/><span style={{fontSize: '0.7rem', fontWeight: 'normal'}}>(2× TAX DUE)</span></TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', py: 1.5 }}>DISC/PEN</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary', fontSize: '0.75rem', py: 1.5 }}>TOTAL</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {yearBreakdown.map((item, index) => (
                          <TableRow key={index} hover>
                            <TableCell sx={{ py: 1 }}>
                              <Typography variant="body2" fontWeight="bold">{item.year}</Typography>
                              {item.effectivityYear && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  (AV {item.effectivityYear})
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1 }}>₱{item.assessedValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            <TableCell sx={{ py: 1 }}>₱{item.taxDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            <TableCell sx={{ py: 1 }}>₱{round2(item.fullPayment * paymentRatio).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                            <TableCell sx={{ py: 1 }}>
                              {item.discount > 0 ? (
                                <Box>
                                  <Typography variant="body2" color="success.main">-₱{formatCurrencyTruncated(round2(item.discount * paymentRatio))}</Typography>
                                  <Typography variant="caption" color="text.secondary">({item.discountPercent}% off)</Typography>
                                </Box>
                              ) : item.penalty > 0 ? (
                                <Box>
                                  <Typography variant="body2" color="error.main">+₱{formatCurrencyTruncated(round2(item.penalty * paymentRatio))}</Typography>
                                  <Typography variant="caption" color="text.secondary">({item.penaltyPercent}% pen)</Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2">₱0.00</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1, fontWeight: 'bold' }}>
                              ₱{formatCurrencyTruncated(round2(item.fullPayment * paymentRatio) - round2(item.discount * paymentRatio) + round2(item.penalty * paymentRatio))}
                            </TableCell>
                          </TableRow>
                        ))}
                        
                        <TableRow sx={{ bgcolor: 'success.lighter', '& td': { fontWeight: 'bold', color: 'success.dark', borderTop: '2px solid', borderColor: 'success.main' } }}>
                          <TableCell colSpan={3} align="right">TOTALS</TableCell>
                          <TableCell>₱{columnTotals.full.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                          <TableCell>₱{formatCurrencyTruncated(columnTotals.discPen)}</TableCell>
                          <TableCell>₱{formatCurrencyTruncated(columnTotals.total)}</TableCell>
                        </TableRow>
                        
                        {paymentRatio < 1 && (
                          <TableRow>
                            <TableCell colSpan={6} align="right">
                              SELECTED PAYORS SHARE ({selectedPayors.length}/{availablePayors.length}): <strong>₱{formatCurrencyTruncated(round2(calculatedTax * paymentRatio))}</strong>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="large"
                      onClick={handlePrintCalculation}
                      startIcon={<PrintIcon />}
                      sx={{ flexShrink: 0 }}
                    >
                      Print
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={handleProcessPayment}
                      disabled={isProcessing}
                      startIcon={<PaymentIcon />}
                    >
                      Process Payment (₱{formatCurrencyTruncated(getActualPaymentAmount())})
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', opacity: 0.7 }}>
                  <CalculateIcon sx={{ fontSize: 60, mb: 2 }} />
                  <Typography variant="h6">Ready to Calculate</Typography>
                  <Typography variant="body2">Set parameters and click Calculate Tax</Typography>
                </Box>
              )}
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon color="primary" /> Payment History
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell>OR Number</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Years</TableCell>
                    <TableCell>Paid By</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingHistory ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">Loading history...</TableCell>
                    </TableRow>
                  ) : paymentHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No payment history found.</TableCell>
                    </TableRow>
                  ) : (
                    paymentHistory.map((payment) => (
                      <TableRow key={payment.id || payment.payment_id}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{payment.or_number}</TableCell>
                        <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                        <TableCell>₱{parseFloat(payment.amount_paid).toLocaleString('en-US', { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell>{payment.year_from} - {payment.year_to}</TableCell>
                        <TableCell>{payment.payer_name || '-'}</TableCell>
                        <TableCell>
                          <IconButton 
                            color="error" 
                            size="small" 
                            onClick={() => handleDeletePayment(payment)}
                            disabled={deletingPaymentId === (payment.id || payment.payment_id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PropertyPaymentTab;
